export interface SignificantFigureResult {
  originalNumber: string;
  significantDigits: number;
  significantFigures: string;
  scientificNotation: string;
  roundedResults: {
    round: string;
    ceil: string;
    floor: string;
  };
  errorAnalysis: {
    relativeError: number;
    absoluteError: number;
  };
}

export function analyzeSignificantFigures(input: string): SignificantFigureResult | null {
  if (!input || input.trim() === '') {
    return null;
  }

  const trimmedInput = input.trim();
  
  // 数値の形式をチェック
  const numberPattern = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;
  if (!numberPattern.test(trimmedInput)) {
    return null;
  }

  const num = parseFloat(trimmedInput);
  if (isNaN(num)) {
    return null;
  }

  // 有効数字の数を計算
  const significantDigits = countSignificantFigures(trimmedInput);
  
  // 有効数字のみで表した数値
  const significantFigures = formatWithSignificantFigures(num, significantDigits);
  
  // 科学表記
  const scientificNotation = formatScientificNotation(num, significantDigits);
  
  // 丸め処理の結果
  const roundedResults = {
    round: formatWithSignificantFigures(Math.round(num * Math.pow(10, significantDigits - 1)) / Math.pow(10, significantDigits - 1), significantDigits),
    ceil: formatWithSignificantFigures(Math.ceil(num * Math.pow(10, significantDigits - 1)) / Math.pow(10, significantDigits - 1), significantDigits),
    floor: formatWithSignificantFigures(Math.floor(num * Math.pow(10, significantDigits - 1)) / Math.pow(10, significantDigits - 1), significantDigits)
  };

  // 誤差解析
  const errorAnalysis = calculateErrorAnalysis(num, significantDigits);

  return {
    originalNumber: trimmedInput,
    significantDigits,
    significantFigures,
    scientificNotation,
    roundedResults,
    errorAnalysis
  };
}

function countSignificantFigures(input: string): number {
  // 科学表記の場合は指数部分を除去
  const basePart = input.replace(/[eE][+-]?\d+$/, '');
  
  // 先頭の0を除去（小数点の前）
  let cleaned = basePart.replace(/^0+/, '');
  
  // 小数点がある場合の処理
  if (cleaned.includes('.')) {
    // 小数点の後の末尾の0は有効数字に含まれる
    return cleaned.replace('.', '').length;
  } else {
    // 小数点がない場合、末尾の0は有効数字に含まれない
    return cleaned.replace(/0+$/, '').length;
  }
}

function formatWithSignificantFigures(num: number, significantDigits: number): string {
  if (num === 0) return '0';
  
  const magnitude = Math.floor(Math.log10(Math.abs(num))) + 1;
  
  if (magnitude >= significantDigits) {
    // 整数部分が有効数字より大きい場合
    return num.toExponential(significantDigits - 1);
  } else {
    // 小数点以下に有効数字がある場合
    const decimalPlaces = significantDigits - magnitude;
    return num.toFixed(Math.max(0, decimalPlaces));
  }
}

function formatScientificNotation(num: number, significantDigits: number): string {
  if (num === 0) return '0';
  
  const magnitude = Math.floor(Math.log10(Math.abs(num)));
  const coefficient = num / Math.pow(10, magnitude);
  
  return `${coefficient.toFixed(significantDigits - 1)} × 10^${magnitude}`;
}

function calculateErrorAnalysis(num: number, significantDigits: number): { relativeError: number; absoluteError: number } {
  const magnitude = Math.abs(num) > 0 ? Math.floor(Math.log10(Math.abs(num))) + 1 : 0;
  const absoluteError = Math.pow(10, magnitude - significantDigits) / 2;
  const relativeError = absoluteError / Math.abs(num) * 100;
  
  return {
    relativeError: isFinite(relativeError) ? relativeError : 0,
    absoluteError
  };
}

// 物理・化学計算での有効桁処理
export function applySignificantFiguresToCalculation(numbers: number[], operation: 'add' | 'subtract' | 'multiply' | 'divide'): string {
  if (numbers.length === 0) return '';
  
  if (operation === 'add' || operation === 'subtract') {
    // 加減算では、小数点以下の桁数が最も少ないものに合わせる
    const decimalPlaces = numbers.map(num => {
      const str = num.toString();
      const decimalIndex = str.indexOf('.');
      return decimalIndex === -1 ? 0 : str.length - decimalIndex - 1;
    });
    const minDecimalPlaces = Math.min(...decimalPlaces);
    
    let result = numbers[0];
    for (let i = 1; i < numbers.length; i++) {
      if (operation === 'add') {
        result += numbers[i];
      } else {
        result -= numbers[i];
      }
    }
    
    return result.toFixed(minDecimalPlaces);
  } else {
    // 乗除算では、有効数字が最も少ないものに合わせる
    const significantDigits = numbers.map(num => countSignificantFigures(num.toString()));
    const minSignificantDigits = Math.min(...significantDigits);
    
    let result = numbers[0];
    for (let i = 1; i < numbers.length; i++) {
      if (operation === 'multiply') {
        result *= numbers[i];
      } else {
        result /= numbers[i];
      }
    }
    
    return formatWithSignificantFigures(result, minSignificantDigits);
  }
} 